<template>
  <section id="whyUs" class="py-24 bg-white dark:bg-dark-surface transition-colors duration-300">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref="sectionRef">
      <div class="text-center mb-16 max-w-3xl mx-auto">
        <h2 class="text-base font-semibold text-accent tracking-wide uppercase transition-colors duration-300">{{ t('whyUs.subtitle') }}</h2>
        <p class="mt-2 text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl transition-colors duration-300">{{ t('whyUs.title') }}</p>
      </div>
      
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
        <div v-for="(stat, index) in stats" :key="index" class="text-center p-6 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/50 dark:to-dark-surface/50 border border-indigo-100 dark:border-indigo-900/50 hover:shadow-indigo-200/50 dark:hover:shadow-indigo-900/50 hover:shadow-lg rounded-2xl transition-all duration-300">
          <div class="text-5xl sm:text-6xl font-extrabold text-accent mb-3 tracking-tight transition-colors duration-300">
            {{ formatStatValue(stat.value, index) }}
          </div>
          <div class="text-sm font-medium text-gray-600 dark:text-gray-400 transition-colors duration-300">{{ stat.label }}</div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        <div v-for="(reason, index) in tm('whyUs.reasons')" :key="index" class="text-center animate-on-scroll-scale">
          <div class="w-16 h-16 bg-primary/10 dark:bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-colors duration-300">
            <CheckCircle class="w-8 h-8 text-primary dark:text-accent transition-colors duration-300" />
          </div>
          <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ reason.title }}</h3>
          <p class="text-base text-gray-600 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ reason.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CheckCircle } from 'lucide-vue-next'
import { useAnimateOnScroll } from '@/composables/useAnimateOnScroll'

const { t, tm } = useI18n({ useScope: 'global' })
const sectionRef = ref(null)
const { observe, elements } = useAnimateOnScroll()
const { observeElements: observeScaleElements } = useAnimateOnScroll(null, { animateClass: 'animate-in-scale' })
const animatedValues = ref([])
const stats = tm('whyUs.stats') || []

onMounted(() => {
  if (sectionRef.value) {
    observe(sectionRef.value)
  }
  observeScaleElements('.animate-on-scroll-scale')
  
  animatedValues.value = stats.map(s => {
    if (!s || !s.value || typeof s.value !== 'string') return 0
    return (s.value.includes('待確認') || isNaN(parseInt(s.value))) ? s.value : 0
  })
})

watch(() => elements.value, (newElements) => {
  const el = newElements.find(e => e.el === sectionRef.value)
  if (el && el.isVisible) {
    stats.forEach((s, i) => {
      if (!s || !s.value || typeof s.value !== 'string') return
      const targetStr = s.value
      if (targetStr.includes('待確認') || isNaN(parseInt(targetStr))) return
      
      const target = parseInt(targetStr.replace(/\D/g, ''))
      const suffix = targetStr.replace(/\d/g, '')
      const duration = 2000
      const start = performance.now()
      
      const animate = (time) => {
        const progress = Math.min((time - start) / duration, 1)
        const current = Math.floor(progress * target)
        animatedValues.value[i] = current + suffix
        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          animatedValues.value[i] = targetStr
        }
      }
      requestAnimationFrame(animate)
    })
  }
}, { deep: true })

const formatStatValue = (originalValue, index) => {
  if (!originalValue || typeof originalValue !== 'string' || originalValue.includes('待確認') || isNaN(parseInt(originalValue))) {
    return originalValue
  }
  return animatedValues.value[index] || 0
}
</script>
